// Phase H.12b — Footnote & endnote inline atom nodes.
//
// Two near-identical inline atoms that live in the body editor only (the
// header/footer MiniEditor never imports them so the brief's "body-only"
// constraint is enforced by construction).
//
// Serialization:
//   <span class="footnote" data-fn-content="…"></span>
//   <span class="endnote"  data-en-content="…"></span>
//
// The chip is rendered EMPTY in the editor — the visible number comes from a
// CSS counter on `.ProseMirror` (`counter-increment: footnote/endnote` +
// `::before { content: counter(footnote); }`).  Auto-renumbering is then
// "free" when chips are reordered or removed.
//
// The backend `_expand_notes_for_pdf` helper copies `data-fn-content` into
// the span's inner text before WeasyPrint sees the doc, so the
// `float: footnote` rule hoists the body correctly.  For endnotes it
// replaces each span with a small `<sup class="endnote-ref"></sup>` marker
// and appends a "Notes" section.

import { Node, mergeAttributes } from "@tiptap/core";

function _makeNoteNode({ name, className, dataAttr, commandName, removeCommand }) {
    return Node.create({
        name,
        group: "inline",
        inline: true,
        atom: true,
        selectable: true,
        // Phase H.12b — atomicity: Backspace on a selected chip removes it
        // as a single token (TipTap default for atom inline nodes).

        addAttributes() {
            return {
                content: {
                    default: "",
                    parseHTML: (el) => el.getAttribute(dataAttr) || "",
                    renderHTML: (attrs) => ({ [dataAttr]: attrs.content || "" }),
                },
            };
        },

        parseHTML() {
            return [{ tag: `span.${className}` }];
        },

        renderHTML({ HTMLAttributes }) {
            return [
                "span",
                mergeAttributes(HTMLAttributes, {
                    class: className,
                    spellcheck: "false",
                    // `data-note-kind` is consumed by the editor click handler
                    // so it can open the popover with the right variant.
                    "data-note-kind": className,
                }),
                // Empty inner text — chip number is purely CSS-counter
                // driven in the editor.  Backend `_expand_notes_for_pdf`
                // rewrites this for the PDF pipeline.
                "",
            ];
        },

        renderText({ node }) {
            // Plain-text fallback (only used in the editor's getText() path
            // or when copy-pasting outside ProseMirror).
            return `[${className} ${node.attrs.content || ""}]`;
        },

        addCommands() {
            return {
                [commandName]:
                    (content = "") =>
                    ({ commands }) =>
                        commands.insertContent({
                            type: this.name,
                            attrs: { content: String(content || "") },
                        }),
                [removeCommand]:
                    () =>
                    ({ commands, state }) => {
                        const { from, to } = state.selection;
                        // Walk the selection looking for this node type.
                        let pos = null;
                        state.doc.nodesBetween(from, to, (n, p) => {
                            if (n.type.name === name && pos === null) pos = p;
                        });
                        if (pos === null) return false;
                        return commands.deleteRange({ from: pos, to: pos + 1 });
                    },
            };
        },
    });
}

export const FootnoteNode = _makeNoteNode({
    name: "footnoteRef",
    className: "footnote",
    dataAttr: "data-fn-content",
    commandName: "insertFootnote",
    removeCommand: "removeFootnoteAtSelection",
});

export const EndnoteNode = _makeNoteNode({
    name: "endnoteRef",
    className: "endnote",
    dataAttr: "data-en-content",
    commandName: "insertEndnote",
    removeCommand: "removeEndnoteAtSelection",
});

export default FootnoteNode;
